/**
 * /network/inbox — Full-width inbox with suggestions sidebar
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Inbox, MessageCircle, UserCheck, UserX, ChevronRight,
  Loader2, Bell, Sparkles, Users, Network, Trophy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { leftTopArrowGradient } from '@/assets/svgs/gradientIcons';

const LV_COL = ['#94a3b8','#7dd3fc','#c4b5fd','#67e8f9','#f0abfc','#62FAE3'];
const lvCol  = lv => LV_COL[Math.min((lv||1)-1,5)];

const fmt = iso => {
  if (!iso) return '';
  const d = new Date(iso), now = new Date(), diff = now - d;
  if (diff < 60000) return 'now';
  if (diff < 3600000) return `${Math.floor(diff/60000)}m`;
  if (diff < 86400000) return `${Math.floor(diff/3600000)}h`;
  return d.toLocaleDateString('en-US', { month:'short', day:'numeric' });
};

function RequestCard({ req, onAccept, onDecline }) {
  const [busy, setBusy] = useState(false);
  const act = async fn => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  return (
    <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
      className="p-4 rounded-2xl border border-[#D0BCFF]/20 bg-[#D0BCFF]/4 hover:bg-[#D0BCFF]/6 transition-all">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0"
          style={{ background:'linear-gradient(135deg,#D0BCFF,#62FAE3)', color:'#080d14' }}>
          {(req.from_display_name||req.from_handle||'?')[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold text-white text-sm">{req.from_display_name||req.from_handle}</span>
            <span className="text-[10px] text-[#8a94a6]">{fmt(req.created_at)}</span>
          </div>
          <p className="text-[11px] text-[#8a94a6]">@{req.from_handle}</p>
          {req.intro && (
            <p className="text-sm text-[#c5cbd6] mt-2 p-3 rounded-xl bg-white/[0.05] border border-[#D0BCFF]/15 leading-relaxed">
              "{req.intro}"
            </p>
          )}
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={() => act(onAccept)} disabled={busy}
              className="font-bold text-[#080d14] text-xs"
              style={{ background:'linear-gradient(106.58deg,#D0BCFF 0%,#62FAE3 100%)' }}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><UserCheck className="w-3.5 h-3.5 mr-1" />Accept</>}
            </Button>
            <Button size="sm" variant="outline" onClick={() => act(onDecline)} disabled={busy}
              className="border-white/10 bg-white/[0.04] text-[#8a94a6] hover:text-white text-xs">
              <UserX className="w-3.5 h-3.5 mr-1" /> Decline
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ThreadCard({ thread, onClick }) {
  return (
    <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
      onClick={onClick}
      className="group cursor-pointer flex items-center gap-3 p-4 border-b border-white/[0.05] last:border-0 hover:bg-white/[0.04] transition-all">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0"
        style={{ background:'linear-gradient(135deg,#BF5AF2,#D0BCFF)', color:'white' }}>
        {(thread.other_display_name||thread.other_handle||'?')[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-bold text-white text-sm">{thread.other_display_name||thread.other_handle}</span>
          {thread.last_at && <span className="text-[10px] text-[#8a94a6] flex-shrink-0">{fmt(thread.last_at)}</span>}
        </div>
        <p className="text-xs text-[#8a94a6] truncate mt-0.5">{thread.last_message||'Conversation started'}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-[#8a94a6] group-hover:text-[#62FAE3] flex-shrink-0 transition-colors" />
    </motion.div>
  );
}

function SuggestCard({ profile, onClick }) {
  const col = lvCol(profile.progress?.level);
  return (
    <div onClick={onClick}
      className="group cursor-pointer flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-white/[0.05] transition-all">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs flex-shrink-0 overflow-hidden"
        style={{ background:`linear-gradient(135deg,${col}cc,${col}44)`, color:'#080d14' }}>
        {profile.avatar_url
          ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
          : (profile.display_name||'?')[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-white truncate">{profile.display_name}</p>
        <p className="text-[10px] text-[#8a94a6]">@{profile.handle}</p>
      </div>
      <ChevronRight className="w-3 h-3 text-[#8a94a6] group-hover:text-[#62FAE3] transition-colors" />
    </div>
  );
}

export const NetworkInboxPage = () => {
  const navigate = useNavigate();
  const [tab, setTab]           = useState('threads');
  const [requests, setRequests] = useState([]);
  const [threads, setThreads]   = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading]   = useState(true);

  const load = useCallback(async () => {
    try {
      const [rr, tr, dr] = await Promise.all([
        api.get('/network/requests'),
        api.get('/network/conversations'),
        api.get('/network?limit=8').catch(() => ({ data:{ profiles:[] } })),
      ]);
      setRequests(rr.data.requests||[]);
      setThreads(tr.data.conversations||[]);
      setSuggestions(dr.data.profiles||[]);
    } catch { toast.error('Failed to load inbox'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const accept  = async req => {
    try { await api.post(`/network/requests/${req.id}/accept`); toast.success('Connection accepted!'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail||'Failed'); }
  };
  const decline = async req => {
    try { await api.post(`/network/requests/${req.id}/decline`); toast.success('Declined'); load(); }
    catch { toast.error('Failed'); }
  };

  const pendingCount = requests.length;

  return (
    <div className="min-h-screen bg-transparent text-white relative overflow-x-hidden">
      <div className="fixed top-0 left-0 w-[700px] pointer-events-none z-0 opacity-35"
        style={{ transform:'translate(-20%,-20%)' }} dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }} />

      {/* ── Header ── */}
      <div className="relative px-5 sm:px-8 pt-6 pb-4 border-b border-white/[0.06]"
        style={{ background:'linear-gradient(180deg,rgba(191,90,242,0.04) 0%,transparent 100%)' }}>
        <div className="flex items-start justify-between flex-wrap gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center border border-[#D0BCFF]/20"
                style={{ background:'rgba(191,90,242,0.1)' }}>
                <Inbox className="w-3.5 h-3.5 text-[#D0BCFF]" />
              </div>
              <span className="text-[11px] font-bold tracking-widest text-[#D0BCFF] uppercase">Inbox</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
              <span style={{ background:'linear-gradient(106deg,#D0BCFF 0%,#62FAE3 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                Messages
              </span>
            </h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => navigate('/network')}
              className="border-white/10 bg-white/[0.04] text-[#c5cbd6] hover:text-white hover:bg-white/[0.08]">
              <Network className="w-4 h-4 mr-1" /> Directory
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/network/leaderboard')}
              className="border-white/10 bg-white/[0.04] text-amber-400 hover:text-amber-300">
              <Trophy className="w-4 h-4 mr-1" /> Board
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/[0.07] w-fit relative z-10">
          {[
            { id:'threads', label:'Conversations', Icon:MessageCircle },
            { id:'requests', label:'Requests', Icon:Bell, badge:pendingCount },
          ].map(t => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${tab===t.id ? 'text-[#080d14]' : 'text-[#8a94a6] hover:text-white'}`}
              style={tab===t.id ? { background:'linear-gradient(106.58deg,#D0BCFF 0%,#62FAE3 100%)' } : undefined}>
              <t.Icon className="w-3.5 h-3.5" />
              {t.label}
              {t.badge > 0 && (
                <span className={`text-[10px] px-1.5 rounded-full font-bold ${tab===t.id ? 'bg-[#080d14]/30 text-[#080d14]' : 'bg-[#D0BCFF]/20 text-[#D0BCFF]'}`}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Body: list + sidebar ── */}
      <div className="flex gap-0 relative z-10">
        {/* Main content */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="flex justify-center py-24">
              <Loader2 className="w-7 h-7 animate-spin text-[#62FAE3]" />
            </div>
          ) : tab === 'requests' ? (
            requests.length === 0 ? (
              <div className="text-center py-20 px-6">
                <Bell className="w-12 h-12 mx-auto mb-4 text-[#8a94a6]/30" />
                <p className="text-white font-bold text-lg">No pending requests</p>
                <p className="text-[#8a94a6] text-sm mt-1">When someone wants to connect, they'll show up here.</p>
              </div>
            ) : (
              <div className="p-5 space-y-3">
                <p className="text-xs text-[#8a94a6]">{pendingCount} pending request{pendingCount!==1?'s':''}</p>
                {requests.map(r => (
                  <RequestCard key={r.id} req={r} onAccept={() => accept(r)} onDecline={() => decline(r)} />
                ))}
              </div>
            )
          ) : (
            threads.length === 0 ? (
              <div className="text-center py-20 px-6">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 text-[#8a94a6]/30" />
                <p className="text-white font-bold text-lg">No conversations yet</p>
                <p className="text-[#8a94a6] text-sm mt-1">Accept a message request to start chatting.</p>
                {requests.length > 0 && (
                  <Button className="mt-4" variant="outline" size="sm" onClick={() => setTab('requests')}
                    style={{ borderColor:'rgba(208,188,255,0.3)', color:'#D0BCFF' }}>
                    View {requests.length} request{requests.length!==1?'s':''}
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-white/[0.05]">
                {threads.map(t => (
                  <ThreadCard key={t.id} thread={t} onClick={() => navigate(`/network/inbox/${t.id}`)} />
                ))}
              </div>
            )
          )}
        </div>

        {/* Sidebar */}
        <div className="hidden lg:flex flex-col gap-4 w-72 xl:w-80 flex-shrink-0 p-5 border-l border-white/[0.06]">
          {/* Discover creators */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-hidden">
            <div className="px-4 py-3 border-b border-white/[0.07] flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#62FAE3]" />
              <span className="text-sm font-bold text-white">Discover Creators</span>
            </div>
            {suggestions.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <p className="text-[#8a94a6] text-xs">No suggestions yet.</p>
              </div>
            ) : (
              <div className="p-2">
                {suggestions.map(p => (
                  <SuggestCard key={p.id||p.handle} profile={p} onClick={() => navigate(`/network/${p.handle}`)} />
                ))}
              </div>
            )}
            <div className="px-4 py-2.5 border-t border-white/[0.07]">
              <button type="button" onClick={() => navigate('/network')}
                className="text-xs text-[#62FAE3] hover:underline w-full text-center">Browse all creators →</button>
            </div>
          </div>

          {/* Tips */}
          <div className="rounded-2xl border border-[#D0BCFF]/15 bg-[#D0BCFF]/4 p-4 space-y-2">
            <p className="text-sm font-bold text-[#D0BCFF] flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> Messaging Tips
            </p>
            {[
              'Send a DM request from any public profile',
              'Include a short intro to increase acceptance',
              'Only accepted requests become conversations',
              'Toggle "Accept requests" in your profile',
            ].map(tip => (
              <p key={tip} className="text-[11px] text-[#8a94a6] flex gap-2 leading-relaxed">
                <span className="text-[#D0BCFF] flex-shrink-0">·</span>
                {tip}
              </p>
            ))}
          </div>

          {/* Quick links */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-1">
            <p className="text-xs font-bold text-[#8a94a6] uppercase tracking-wider mb-2">Quick Links</p>
            {[
              { label:'Creator Directory', path:'/network', Icon:Users, col:'#62FAE3' },
              { label:'Leaderboard', path:'/network/leaderboard', Icon:Trophy, col:'#f5c842' },
              { label:'My Profile', path:'/network/me', Icon:Sparkles, col:'#D0BCFF' },
            ].map(({ label, path, Icon, col }) => (
              <button key={path} type="button" onClick={() => navigate(path)}
                className="w-full flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-white/[0.05] transition-all text-left">
                <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color:col }} />
                <span className="text-xs text-[#c5cbd6]">{label}</span>
                <ChevronRight className="w-3 h-3 text-[#8a94a6] ml-auto" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkInboxPage;
